<template>
  <v-container pa-0 fluid grid-list-lg data-qa="list_selector">
    <v-row>
      <v-col v-if="getlistVisible()" cols="12" sm="3">
        <!-- The slot appears above the slot if on mobile and not collapsing -->
        <slot v-if="getSmallAndSelected() && !collapseOnMobile" />
        <bm-text
          v-if="searchable"
          :value.sync="searchFilter"
          :label="searchLabel"
          :prepend-icon="bmIconName('Search')"
          :placeholder="searchPlaceholder"
          :disabled="disabled"
        >
        </bm-text>

        <template v-for="heading in headings">
          <h3 v-if="heading" :key="heading" class="bm-headline">
            {{ heading }}
          </h3>
          <v-divider v-if="heading" :key="heading + 'divider'" />

          <v-list :key="heading + 'list'" class="bm-list">
            <template
              v-for="menuItem in getFilteredAndLimitedListItems(heading)"
            >
              <v-list-item
                :id="getItemText(menuItem)"
                :key="getItemValue(menuItem).toString()"
                :disabled="disabled"
                :class="{ active: value === getItem(menuItem) }"
                @click="emit('update:value', getItem(menuItem))"
              >
                <v-list-item-content class="bm-body">
                  <v-list-item-title class="d-flex">
                    <span class="list-selector-title">{{
                      getItemText(menuItem)
                    }}</span>
                    <div>
                      <slot name="append-name" :item="menuItem" />
                    </div>
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-for="text in getItemSubtitle(menuItem)"
                    :key="text + 'subtitle'"
                    >{{ text }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider :key="getItemValue(menuItem) + 'divider'" />
            </template>
            <template v-if="getFilteredListShowMore(heading)">
              <v-list-item
                :key="heading.toString() + 'show_more'"
                @click="showAll = true"
              >
                <v-list-item-content class="bm-body">
                  <bm-btn
                    btn-class="secondary"
                    :t="$m.keys.filter_show_all"
                  ></bm-btn>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </template>
      </v-col>
      <v-col v-if="value" cols="12" sm="9">
        <div
          v-if="getSmallAndSelected() && collapseOnMobile && !hideBackButton"
          class="bm-list-back"
        >
          <a @click="emit('update:value', null)">
            <bm-icon icon="ArrowBackIos" />
            <span>{{ returnLabel }}</span></a
          >
        </div>
        <slot v-if="!(getSmallAndSelected() && !collapseOnMobile)" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import type { ListItemWithHeader } from "@/components/controls/listselector/BmListSelectorTypes";
import messages from "@/plugins/i18n";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import debounce from "lodash-es/debounce";
import BmBtn from "@/components/controls/input/BmBtn.vue";
import { ref, watch } from "vue";
import { screenSize } from "@/vue23switching/screenSize";
import { bmIconName } from "@/components/controls/bmIconMap";

interface BmListSelectorProps {
  returnLabel?: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  headings?: string[];
  items?: object[] | ListItemWithHeader[];
  itemText: VuetifyPropertyArg;
  itemValue: VuetifyPropertyArg;
  maxItems?: number;
  itemSubtitle?: VuetifyPropertyArg;
  value?: object | string | null;
  searchable?: boolean;
  defaultSelectFirstItem?: boolean;
  hideList?: boolean;
  hideBackButton?: boolean;
  collapseOnMobile?: boolean;
  sortBy?: ((a: object, b: object) => number) | undefined;
  disabled?: boolean;
}

interface BmListSelectorEvents {
  (event: "update:value", value: object | string | null): void;
}

const props = withDefaults(defineProps<BmListSelectorProps>(), {
  returnLabel: messages.back,
  searchLabel: messages.search,
  searchPlaceholder: "",
  headings: () => [""],
  items: () => [],
  maxItems: 10,
  searchable: true,
  defaultSelectFirstItem: true,
  hideList: false,
  hideBackButton: false,
  collapseOnMobile: true,
  disabled: false,
});

const emit = defineEmits<BmListSelectorEvents>();

const searchFilter = ref("");
const firstLoad = ref(true);
const showAll = ref(false);

const selectNewItem = debounce(() => {
  const filteredItems = getFilteredListItems();
  if (screenSize.smAndUp && filteredItems[0]) {
    emit("update:value", getItem(filteredItems[0]));
  } else {
    emit("update:value", null);
  }
}, 300);

watch(
  () => props.items,
  (val) => {
    // Default select the first item in 'desktop mode' and on first load only
    if (
      props.items.length &&
      firstLoad.value &&
      props.defaultSelectFirstItem &&
      screenSize.smAndUp
    ) {
      firstLoad.value = false;
      for (const heading of props.headings) {
        for (const item of val) {
          emit("update:value", getItem(item));
          return;
        }
      }
    }
  },
  { immediate: true }
);

watch(searchFilter, () => {
  showAll.value = false;
  selectNewItem();
});

watch(
  () => props.value,
  () => {
    if (props.value === null) selectNewItem();
  }
);

function isItemWithHeader(
  item: object | ListItemWithHeader
): item is ListItemWithHeader {
  return (item as ListItemWithHeader).heading !== undefined;
}

function getItem(item: object | ListItemWithHeader) {
  return isItemWithHeader(item) ? item.item : item;
}

function getItemText(item: object | ListItemWithHeader): string {
  return isItemWithHeader(item)
    ? getPropertyFromItem(item.item, props.itemText)
    : getPropertyFromItem(item, props.itemText);
}

function getItemSubtitle(item: object | ListItemWithHeader): string[] {
  /** Prevent getPropertyFromItem returning just an object, and make sure
   *  the title is an array when looping through subtitle entries.
   **/
  if (!props.itemSubtitle) {
    return [];
  }

  const subtitle = isItemWithHeader(item)
    ? getPropertyFromItem(item.item, props.itemSubtitle)
    : getPropertyFromItem(item, props.itemSubtitle);

  return Array.isArray(subtitle) ? subtitle : [subtitle];
}

function getItemValue(item: object | ListItemWithHeader): string {
  return isItemWithHeader(item)
    ? getPropertyFromItem(item.item, props.itemValue)
    : getPropertyFromItem(item, props.itemValue);
}

function getFilteredAndLimitedListItems(heading?: string) {
  return getFilteredListItems(heading).slice(
    0,
    showAll.value ? Number.MAX_SAFE_INTEGER : props.maxItems
  );
}

function getFilteredListItems(heading?: string) {
  return props.items
    .filter((o) => !heading || !isItemWithHeader(o) || o.heading === heading)
    .filter((o) =>
      getItemText(o)
        .toLocaleLowerCase()
        .includes(searchFilter.value.toLocaleLowerCase())
    )
    .sort(props.sortBy);
}

function getFilteredListShowMore(heading?: string): boolean {
  return (
    !showAll.value && getFilteredListItems(heading).length > props.maxItems
  );
}

function getlistVisible(): boolean {
  return (!props.hideList && screenSize.smAndUp) ||
    (!props.value && props.items) ||
    (!screenSize.smAndUp && !props.collapseOnMobile)
    ? true
    : false;
}

function getSmallAndSelected() {
  return !!(screenSize.xs && props.value && props.items);
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

.bm-list.theme--light.v-list
  background-color: transparent
  margin-top: 15px
  margin-bottom: 20px

.bm-list .active
  background-color: $bm-color-brand02

:deep(.bm-list .v-list-item.active .v-list-item__subtitle)
  color: white !important

:deep(.bm-list .v-list-item.active .v-list-item__title)
  color: white !important

:deep(.bm-list .v-list-item .v-list-item__title)
  color: $bm-color-text02

.bm-list-back
  margin-bottom: 1em
  margin-top: 1em

.bm-list-back span
  text-decoration: underline
  vertical-align: middle

.list-selector-title
  flex: 1
  min-width: 0
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

/* or some value */
</style>
